import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Copy, MessageSquare, Check, AlertCircle, X } from 'lucide-react';
import ScoreRing from '../components/ScoreRing';
import ModuleExplainer from '../components/ModuleExplainer';
import MentorFinder from '../components/MentorFinder';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { Users as A1, MessageSquare as A2, Search as A3, Calendar as A4, Heart as A5, Send as A6 } from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api/career-growth`;

// Nine advisor roles, nine colours, the board should look like a board, not a list.
const ROLE_HUES = {
  transition_mentor: '#22d3ee', industry_mentor: '#60a5fa', career_mentor: '#a78bfa',
  skill_mentor: '#34d399', peer_mentor: '#a3e635', connector: '#fbbf24',
  recruiter: '#fb923c', sponsor: '#f472b6', leadership_mentor: '#f87171',
};
const JOURNEY_HUES = ['#22d3ee', '#60a5fa', '#a78bfa', '#f472b6', '#fb923c', '#fbbf24', '#a3e635', '#34d399'];

const EMPTY = { id: '', name: '', organization: '', role: '', how_met: '', last_contact: '', notes: '', advice_received: '' };

const Field = ({ label, value, hint }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-slate-300">{label}</p>
    {value
      ? <p className="text-sm text-slate-100 mt-0.5 whitespace-pre-line">{value}</p>
      : <p className="text-sm text-slate-300 italic mt-0.5">{hint}</p>}
  </div>
);

export default function ConnectMePage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [out, setOut] = useState({ name: '', organization: '', role: 'career_mentor', shared: '', ask: '', channel: 'linkedin_dm' });
  const [formats, setFormats] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const [res, f] = await Promise.all([
        axios.get(`${API}/connect`, getAuthHeader()),
        axios.get(`${API}/connect/outreach-formats`, getAuthHeader()),
      ]);
      setD(res.data);
      setFormats(f.data.formats);
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const addPerson = async () => {
    if (!form.name.trim() || !form.role) { toast.error('Name and the role they play are required.'); return; }
    setBusy(true);
    try {
      const editing = !!form.id;
      const res = await axios.post(`${API}/connect/relationship`, form, getAuthHeader());
      setD(res.data);
      setForm(EMPTY);
      if (editing) toast.success('Updated.');
      else celebrate('Added to your board. Relationships are how most jobs actually arrive.');
    } catch { toast.error('Could not save that person.'); }
    finally { setBusy(false); }
  };

  const removePerson = async (id) => {
    const res = await axios.delete(`${API}/connect/relationship/${id}`, getAuthHeader());
    setD(res.data);
    setSelected(null);
  };

  const editPerson = (p) => {
    setSelected(null);
    setForm({ ...EMPTY, ...p });
    setTimeout(() => document.querySelector('[data-testid="add-person-form"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
  };

  const draftTo = (p) => {
    setSelected(null);
    setOut(o => ({ ...o, name: p.name || '', organization: p.organization || '', role: p.role || o.role }));
    setTimeout(() => document.querySelector('[data-testid="outreach-card"]')
      ?.scrollIntoView({ behavior: 'smooth' }), 80);
  };

  const makeDraft = async () => {
    if (!out.name.trim()) { toast.error('Who are you reaching out to?'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/connect/outreach`, out, getAuthHeader());
      setDraft(res.data);
    } catch { toast.error('Could not draft that message.'); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  const roles = d.map || [];

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="connect-arrival"
        eyebrow="My Mentors & Network"
        title="Most jobs come through people."
        subtitle="Not networking in the awful sense. A short list of specific people worth knowing, a reason to contact each one, and the exact message to send, then a record of who replied."
        accent="#34d399"
        second="#22d3ee"
        ctaLabel="Build my network"
        narration={"This is My Mentors and Network. Most opportunities arrive through people rather than applications, and almost nobody is taught how to ask. So we keep it concrete: who is genuinely worth knowing for the path you are on, why they would want to hear from you, and the message to send, short, specific and human. Then you track who replied and what came of it, so relationships get built instead of forgotten."}
        facts={[
          { icon: A1, head: "Specific people", sub: "Roles and names, not a vague instruction to network" },
          { icon: A6, head: "The exact message", sub: "Short, specific, and not asking for a job" },
          { icon: A5, head: "Kept warm", sub: "A record of who replied and what to do next" }
        ]}
        modules={[
          { icon: A3, label: "Who to know", line: "Mentors, alumni, people doing the job you want", hue: "#34d399" },
          { icon: A2, label: "What to say", line: "Openers that get replies, in your own voice", hue: "#22d3ee" },
          { icon: A6, label: "Outreach tracker", line: "Sent, replied, met, nothing lost in a mailbox", hue: "#fbbf24" },
          { icon: A4, label: "Conversations", line: "Questions worth asking once someone says yes", hue: "#a78bfa" },
          { icon: A5, label: "Follow-up", line: "How to stay in touch without being a nuisance", hue: "#f472b6" },
          { icon: A1, label: "Your circle", line: "The people already around you, mapped", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader
        title="Connect Me, Mentors & Network"
        description="Mentorship is developed, not requested. Build the board of people who make the move possible, and find them on LinkedIn, in associations and in alumni networks."
      />

      <ModuleExplainer id="connect" />

      {/* Network readiness */}
      <GlassCard className="mb-6" data-testid="network-readiness">
        <div className="flex flex-wrap items-center gap-8">
          <ScoreRing value={d.network_readiness} label="Network" sublabel={`${d.roles_filled}/${d.roles_total} advisor roles`}
            palette="emerald" testId="network-score" />
          <div className="flex-1 min-w-[240px]">
            <p className="text-sm text-white mb-3">{d.roles_filled} of {d.roles_total} advisor roles filled</p>
            <div className="flex gap-1.5">
              {roles.map(r => {
                const hue = ROLE_HUES[r.key] || '#22d3ee';
                return (
                  <div key={r.key} title={r.label} className="h-2.5 flex-1 rounded-full"
                    style={r.filled
                      ? { background: hue, boxShadow: `0 0 12px ${hue}` }
                      : { background: 'rgba(148,163,184,.18)' }} />
                );
              })}
            </div>
            <p className="text-xs text-slate-300 mt-3">{d.note}</p>
          </div>
          {d.priority_gap && (
            <div className="p-4 rounded-xl border border-amber-400/40 max-w-xs" data-testid="priority-gap"
              style={{ background: 'linear-gradient(135deg, rgba(251,191,36,.16), rgba(244,114,182,.12))', boxShadow: '0 0 26px rgba(251,191,36,.18)' }}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> Close this gap first
              </p>
              <p className="text-sm font-semibold text-white mt-1">{d.priority_gap.label}</p>
              <p className="text-xs text-amber-100/80 mt-1">{d.priority_gap.why}</p>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Relationship journey */}
      <GlassCard className="mb-6" data-testid="relationship-journey">
        <h3 className="text-base md:text-lg font-semibold text-white mb-4">The relationship journey</h3>
        <div className="flex flex-wrap items-center gap-2">
          {(d.journey || []).map((s, i) => {
            const hue = JOURNEY_HUES[i % JOURNEY_HUES.length];
            return (
              <span key={s} className="flex items-center gap-2">
                <span className="px-3.5 py-1.5 rounded-full text-xs text-white"
                  style={{ background: `${hue}24`, border: `1px solid ${hue}66`, boxShadow: `0 0 14px ${hue}2e` }}>{s}</span>
                {i < d.journey.length - 1 && <span className="text-slate-300">→</span>}
              </span>
            );
          })}
        </div>
        <p className="text-xs text-slate-300 mt-3">Nobody skips from Discover to Sponsorship. Each stage earns the next.</p>
      </GlassCard>

      {/* Board of advisors */}
      <GlassCard className="mb-6" data-testid="board-of-advisors">
        <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-cyan-300" /> Your personal board of advisors
        </h3>
        <div className="grid md:grid-cols-3 gap-3">
          {roles.map(r => {
            const hue = ROLE_HUES[r.key] || '#22d3ee';
            return (
              <div key={r.key} data-testid={`role-${r.key}`}
                className="relative overflow-hidden p-4 rounded-xl border transition-colors duration-200"
                style={r.filled
                  ? { borderColor: `${hue}80`, background: `linear-gradient(160deg, ${hue}1f, rgba(15,23,42,.55))`, boxShadow: `0 0 22px ${hue}1f` }
                  : { borderColor: 'rgba(148,163,184,.22)', background: 'rgba(30,41,59,.4)' }}>
                <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: hue, opacity: r.filled ? 1 : 0.4 }} />
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-white">{r.label}</p>
                  {r.filled
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full text-slate-900 font-semibold" style={{ background: hue }}>{r.filled}</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/60 text-slate-300">Open</span>}
                </div>
                <p className="text-xs text-slate-300 mt-1">{r.why}</p>
                {r.people.map(p => (
                  <div key={p.id} className="mt-2 flex items-center gap-1 rounded-lg bg-slate-900/60 border border-slate-700 hover:border-slate-500 transition-colors duration-200">
                    <button onClick={() => setSelected({ ...p, roleLabel: r.label, roleWhy: r.why, hue })}
                      data-testid={`person-${p.id}`}
                      className="flex-1 min-w-0 text-left px-2 py-1.5 group/person">
                      <span className="block text-xs text-slate-100 truncate group-hover/person:text-white">
                        {p.name}
                      </span>
                      {p.organization && (
                        <span className="block text-[10px] text-slate-300 truncate">{p.organization}</span>
                      )}
                      <span className="block text-[10px] mt-0.5" style={{ color: hue }}>View details →</span>
                    </button>
                    <button onClick={() => removePerson(p.id)} data-testid={`remove-${p.id}`}
                      aria-label={`Remove ${p.name}`}
                      className="px-2 py-1.5 text-slate-300 hover:text-rose-400 transition-colors duration-200">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button onClick={() => setForm({ ...EMPTY, role: r.key })} data-testid={`fill-${r.key}`}
                  className="mt-3 inline-flex items-center gap-1 text-xs hover:opacity-80 transition-opacity duration-200" style={{ color: hue }}>
                  <Plus className="w-3 h-3" /> Add someone here
                </button>
              </div>
            );
          })}
        </div>

        {form.role && (
          <div className="mt-5 p-4 rounded-xl border border-cyan-400/30 bg-slate-900/60" data-testid="add-person-form">
            <p className="text-sm text-white mb-3">
              {form.id ? 'Editing ' : 'Adding a '}{form.id ? form.name : roles.find(r => r.key === form.role)?.label}
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Their name" data-testid="input-person-name" />
              <Input value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} placeholder="Where they work" data-testid="input-person-org" />
              <Input value={form.how_met} onChange={e => setForm({ ...form, how_met: e.target.value })} placeholder="How you met / found them" data-testid="input-person-met" />
              <Input value={form.last_contact} onChange={e => setForm({ ...form, last_contact: e.target.value })} placeholder="Last contact (e.g. June 2026)" data-testid="input-person-contact" />
              <div className="md:col-span-2">
                <Input value={form.advice_received} onChange={e => setForm({ ...form, advice_received: e.target.value })}
                  placeholder="Advice they gave you (so you can report back later)" data-testid="input-person-advice" />
              </div>
              <div className="md:col-span-2">
                <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Notes, anything you want to remember about them" data-testid="input-person-notes" />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={addPerson} disabled={busy} data-testid="save-person-btn">
                {busy ? 'Saving…' : form.id ? 'Save changes' : 'Add to my board'}
              </Button>
              <Button variant="ghost" onClick={() => setForm(EMPTY)} data-testid="cancel-person-btn">Cancel</Button>
            </div>
          </div>
        )}
      </GlassCard>

      {selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0a1729]/75 backdrop-blur-sm p-4"
          onClick={() => setSelected(null)} data-testid="person-detail-overlay">
          <div onClick={e => e.stopPropagation()} data-testid="person-detail"
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border bg-slate-900/95 p-6 relative"
            style={{ borderColor: `${selected.hue}66`, boxShadow: `0 24px 70px -20px ${selected.hue}55` }}>
            <span className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl" style={{ background: selected.hue }} />
            <button onClick={() => setSelected(null)} data-testid="close-person-detail"
              className="absolute top-4 right-4 text-slate-300 hover:text-white transition-colors duration-200">
              <X className="w-4 h-4" />
            </button>
            <p className="text-[11px] px-2 py-0.5 rounded-full inline-block font-semibold text-slate-900"
              style={{ background: selected.hue }}>{selected.roleLabel}</p>
            <h3 className="text-xl font-semibold text-white mt-2">{selected.name}</h3>
            {selected.organization && <p className="text-sm text-slate-300">{selected.organization}</p>}
            <p className="text-xs text-slate-300 mt-2 italic">{selected.roleWhy}</p>

            <div className="mt-5 space-y-4">
              <Field label="How you met them" value={selected.how_met}
                hint="Not recorded yet, worth adding, it's the line you open with next time." />
              <Field label="Advice they gave you" value={selected.advice_received}
                hint="Nothing logged. When someone gives you advice, write it here, reporting back on it is what turns a contact into a mentor." />
              <Field label="Notes" value={selected.notes} hint="No notes yet." />
              <Field label="Last contact" value={selected.last_contact}
                hint="Unknown. If it's been more than a few months, a short check-in is overdue." />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Button onClick={() => draftTo(selected)} data-testid="draft-to-person-btn">
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Draft a message to them
              </Button>
              <Button variant="ghost" onClick={() => editPerson(selected)} data-testid="edit-person-btn">
                Edit details
              </Button>
              <Button variant="ghost" onClick={() => removePerson(selected.id)} data-testid="delete-person-btn"
                className="text-rose-300 hover:text-rose-200">
                Remove from board
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mentor finder */}
      <div className="mb-6">
        <MentorFinder onDraftFor={(role) => {
          setOut(o => ({ ...o, role }));
          document.querySelector('[data-testid="outreach-card"]')?.scrollIntoView({ behavior: 'smooth' });
        }} />
      </div>

      {/* Outreach */}
      <GlassCard data-testid="outreach-card">
        <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-1">
          <MessageSquare className="w-4 h-4 text-cyan-300" /> Outreach drafting
        </h3>
        <p className="text-xs text-slate-300 mb-4">Written from what the platform already knows about you, then scored before you send it.</p>

        <div className="grid md:grid-cols-2 gap-3">
          <Input value={out.name} onChange={e => setOut({ ...out, name: e.target.value })} placeholder="Who are you writing to?" data-testid="input-outreach-name" />
          <Input value={out.organization} onChange={e => setOut({ ...out, organization: e.target.value })} placeholder="Their organization" data-testid="input-outreach-org" />
          <select value={out.role} onChange={e => setOut({ ...out, role: e.target.value })} data-testid="select-outreach-role"
            className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500">
            {roles.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <select value={out.channel} onChange={e => setOut({ ...out, channel: e.target.value })} data-testid="select-outreach-channel"
            className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500">
            {formats.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <p className="md:col-span-2 text-[11px] text-slate-300">
            {formats.find(f => f.key === out.channel)?.when} · Keep it to {formats.find(f => f.key === out.channel)?.limit}.
          </p>
          <div className="md:col-span-2">
            <Input value={out.shared} onChange={e => setOut({ ...out, shared: e.target.value })}
              placeholder="Something specific about them (their path, unit, school, a post you read)" data-testid="input-outreach-shared" />
          </div>
          <div className="md:col-span-2">
            <Input value={out.ask} onChange={e => setOut({ ...out, ask: e.target.value })}
              placeholder="Your ask (leave blank and we'll keep it to a 20-minute conversation)" data-testid="input-outreach-ask" />
          </div>
        </div>
        <Button className="mt-4" onClick={makeDraft} disabled={busy} data-testid="draft-outreach-btn">
          {busy ? 'Drafting…' : 'Draft it and score it'}
        </Button>

        {draft && (
          <div className="mt-6 grid lg:grid-cols-2 gap-4" data-testid="outreach-result">
            <div className="p-4 rounded-xl border border-slate-700 bg-slate-900/60">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-slate-300">
                  {draft.format?.label} · {draft.role} · {draft.characters} chars
                </span>
                <button onClick={() => { navigator.clipboard.writeText(draft.draft); toast.success('Copied. Make it sound like you.'); }}
                  data-testid="copy-draft-btn"
                  className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 transition-colors duration-200">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
              <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{draft.draft}</p>
              <ul className="mt-3 space-y-1">
                {(draft.editing_notes || []).map((n, i) => (
                  <li key={i} className="text-[11px] text-slate-300 flex gap-2"><span className="text-cyan-400">•</span>{n}</li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-300 mt-2">{draft.note}</p>
            </div>
            <div className="p-4 rounded-xl border border-slate-700 bg-slate-800/40">
              <div className="flex items-center gap-4 mb-3">
                <ScoreRing value={draft.quality_score} label="Quality" palette={draft.quality_score >= 80 ? 'emerald' : 'amber'}
                  size={130} testId="outreach-score" />
                <p className="text-xs text-slate-300">Every check that passes is one more reason they reply.</p>
              </div>
              <div className="space-y-1.5">
                {draft.checks.map(c => (
                  <div key={c.check} className="flex items-start gap-2 text-xs">
                    <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                      c.pass ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                      {c.pass ? <Check className="w-3 h-3" /> : '!'}
                    </span>
                    <div>
                      <p className={c.pass ? 'text-slate-300' : 'text-white'}>{c.check}</p>
                      {!c.pass && <p className="text-[11px] text-amber-200/80">{c.fix}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </GlassCard>

      {(d.follow_ups || []).length > 0 && (
        <GlassCard className="mt-6" data-testid="follow-ups">
          <h3 className="text-sm font-semibold text-white mb-3">Report back on advice you used</h3>
          <div className="space-y-2">
            {d.follow_ups.map(f => (
              <div key={f.id} className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700">
                <p className="text-sm text-white">{f.name}</p>
                <p className="text-xs text-slate-300">{f.advice_received}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </AppLayout>
  );
}
